// DiceTray — quick dice + custom expressions with advantage/disadvantage. The reveal is
// the payoff: on a roll the d20 tumbles through faces, snaps to the natural result, and
// the total counts up large. A nat 20 bursts (.roll-crit); a nat 1 cracks (.roll-fumble).
// The full transparent breakdown (the engine's RollResult) stays available beneath.
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type { Advantage, RollResult } from "../lib/types";

const QUICK = [20, 12, 10, 8, 6, 4, 100];

type Phase = "idle" | "tumbling" | "done";

export function DiceTray() {
  const [expr, setExpr] = useState("1d20");
  const [adv, setAdv] = useState<Advantage>("normal");
  const [result, setResult] = useState<RollResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [face, setFace] = useState(20);
  const [shownTotal, setShownTotal] = useState(0);
  const [showFace, setShowFace] = useState(true);
  const tumbleTimer = useRef<number | null>(null);
  const countTimer = useRef<number | null>(null);

  const clearTimers = () => {
    if (tumbleTimer.current) { window.clearInterval(tumbleTimer.current); tumbleTimer.current = null; }
    if (countTimer.current) { window.clearInterval(countTimer.current); countTimer.current = null; }
  };
  useEffect(() => () => clearTimers(), []);

  function animateCount(target: number) {
    if (countTimer.current) window.clearInterval(countTimer.current);
    const steps = 16;
    let i = 0;
    countTimer.current = window.setInterval(() => {
      i += 1;
      const t = i / steps;
      const eased = 1 - Math.pow(1 - t, 3);
      setShownTotal(Math.round(target * eased));
      if (i >= steps) {
        if (countTimer.current) window.clearInterval(countTimer.current);
        countTimer.current = null;
        setShownTotal(target);
      }
    }, 26);
  }

  function finish(r: RollResult) {
    if (tumbleTimer.current) { window.clearInterval(tumbleTimer.current); tumbleTimer.current = null; }
    setResult(r);
    if (r.natural != null) setFace(r.natural);
    setPhase("done");
    animateCount(r.total);
    setBusy(false);
  }

  async function roll(expression: string) {
    const isD20 = expression.includes("d20");
    setBusy(true);
    setResult(null);
    setShownTotal(0);
    setShowFace(isD20);
    setPhase("tumbling");
    clearTimers();
    // Tumble the die through random faces while the roll resolves.
    tumbleTimer.current = window.setInterval(() => setFace(1 + Math.floor(Math.random() * 20)), 70);
    const started = Date.now();
    try {
      const r = await api.roll(expression, isD20 ? adv : "normal");
      // Hold the tumble a beat for feel even if the API answers instantly.
      const wait = Math.max(0, 480 - (Date.now() - started));
      window.setTimeout(() => finish(r), wait);
    } catch {
      clearTimers();
      setPhase("idle");
      setBusy(false);
    }
  }

  const crit = phase === "done" && result?.natural === 20;
  const fumble = phase === "done" && result?.natural === 1;

  return (
    <div className="rail-section">
      <div className="rail-head"><span>Dice Tray</span></div>

      <div className="dice-tray__quick">
        {QUICK.map((d) => (
          <button key={d} className="die-btn" disabled={busy} onClick={() => { setExpr(`1d${d}`); roll(`1d${d}`); }}>
            <span className="dn">d{d}</span>
          </button>
        ))}
        <button className="die-btn" disabled={busy} onClick={() => roll(expr)} title="Roll current expression">
          <span className="dn" style={{ fontSize: 13 }}>roll</span>
        </button>
      </div>

      <input className="field" value={expr} onChange={(e) => setExpr(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && roll(expr)} placeholder="2d6+3, 4d6dl1, 1d20…" />

      <div className="flex mt8" style={{ justifyContent: "space-between" }}>
        <div className="adv-toggle">
          <button className={adv === "disadvantage" ? "on dis" : ""} onClick={() => setAdv("disadvantage")}>Dis</button>
          <button className={adv === "normal" ? "on" : ""} onClick={() => setAdv("normal")}>Flat</button>
          <button className={adv === "advantage" ? "on adv" : ""} onClick={() => setAdv("advantage")}>Adv</button>
        </div>
        <button className="btn btn--sm" disabled={busy} onClick={() => roll(expr)}>Roll</button>
      </div>

      {phase !== "idle" && (
        <div className={`die-result roll-reveal${crit ? " crit roll-crit" : ""}${fumble ? " fumble roll-fumble" : ""}`}>
          {showFace && (
            <div className={`roll-reveal__die${phase === "tumbling" ? " is-tumbling" : ""}`}
              aria-hidden
              style={{
                width: 46, height: 46, margin: "0 auto 6px", display: "flex", alignItems: "center",
                justifyContent: "center", borderRadius: 8, fontWeight: 800, fontSize: 22,
                border: "2px solid currentColor",
                transition: phase === "tumbling" ? "none" : "transform 140ms ease-out",
                transform: phase === "tumbling" ? "rotate(-6deg)" : "rotate(0deg) scale(1.06)",
                opacity: 0.92,
              }}>
              {face}
            </div>
          )}
          <div className="dr-total" style={{ fontSize: 30, fontWeight: 800, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
            {phase === "done" ? shownTotal : "…"}
          </div>
          {phase === "done" && result && <div className="dr-detail">{result.detail}</div>}
          {crit && <div className="dr-label">Natural 20!</div>}
          {fumble && <div className="dr-label">Natural 1…</div>}
        </div>
      )}
    </div>
  );
}
